'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import { Button } from '@shared/components/ui/button';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocationsFilters } from '../../hooks/useLocationsFilters';

interface LocationsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  hasTeamData?: boolean;
  className?: string;
}

/** Campo de filtro con label */
const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Ubicaciones.
 * Tipo de ubicación (picking/almacén) y producto.
 */
export const LocationsFiltersPanel = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
  className,
}: LocationsFiltersPanelProps) => {
  const { t } = useTranslation('locationsModule');
  const { searchProductsForCombobox } = useProductSearch();

  const {
    filters,
    handleLocationTypeChange,
    handleProductChange,
    handleProductTeamChange,
    clearAllFilters,
    clientOptions,
    isLoadingClients,
  } = useLocationsFilters({
    initialConditions,
    onChange,
    hasTeamData,
  });

  const locationTypeOptions = useMemo(
    () => [
      { label: t('filter.picking'), value: '2' },
      { label: t('filter.warehouse'), value: '1' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.locationType) count++;
    if (filters.productId) count++;
    if (filters.productTeamIds.length > 0) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      {/* Campos de filtro (el título "Filtros" ya lo muestra FiltersDropdown en la cabecera) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filter.location_type', 'Tipo de Ubicación')}>
          <Combobox
            options={locationTypeOptions}
            value={filters.locationType}
            setValue={handleLocationTypeChange}
            placeholder={t('filter.select_value', 'Seleccionar...')}
            dataTestId="filter-location-type"
          />
        </FilterField>

        <FilterField label={t('filter.product', 'Producto')}>
          <ProductCombobox
            value={filters.productId}
            setValue={handleProductChange}
            placeholder={t('search.products_placeholder', 'Buscar productos...')}
            searchFn={searchProductsForCombobox}
            emptyMessage={t('search.no_products_found', 'No se encontraron productos')}
            loadingMessage={t('loading.filtering_products', 'Filtrando productos...')}
            dataTestId="filter-product"
          />
        </FilterField>

        {hasTeamData && (
          <FilterField label={t('filter.client', 'Cliente')}>
            <MultiSelect
              options={clientOptions}
              value={filters.productTeamIds}
              onValueChange={handleProductTeamChange}
              placeholder={
                isLoadingClients ? t('loading.default', 'Cargando...') : t('filter.all_clients', 'Todos los clientes')
              }
              emptyText={t('filter.not_found', 'Sin resultados')}
              disabled={isLoadingClients}
              dataTestId="filter-product-team"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        )}
      </div>

      {/* Botón limpiar filtros */}
      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="locations-clear-filters"
          >
            {t('filter.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
